/**
 * Safe wrappers around the global `Intl` constructors.
 *
 * Browsers occasionally report non-BCP 47 locale tags (e.g. "en-US@posix" on
 * some POSIX-configured devices, "en_US", empty strings, or vendor-specific
 * tags) via `navigator.language` / `navigator.languages`. Passing those directly
 * to `Intl.NumberFormat`, `Intl.DateTimeFormat`, or `Intl.Locale` throws a
 * RangeError ("Invalid language tag", "Incorrect locale information provided"),
 * which crashes the React component tree when it happens during render.
 *
 * These helpers sanitize the tag once and fall back to 'en-US', so formatting
 * still works instead of crashing. Use them everywhere instead of calling the
 * raw `Intl.*` constructors with user-supplied or browser-reported locales.
 */

export const FALLBACK_LOCALE = 'en-US';

/**
 * Cache keyed by the raw input tag. Values are the canonical tag, or `undefined`
 * when the tag could not be parsed — both outcomes are worth caching, since these
 * helpers run on every render of the components that format numbers and dates.
 *
 * Bounded because this Map is module-level: on the server it lives for the whole
 * process, so an unbounded one would grow forever if a user-controlled tag ever
 * reached it (a `?l=` style param, say). Real cardinality is the handful of tags
 * browsers and i18next produce, so the cap is never reached in practice — it just
 * stops the pathological case. Insertion order makes the oldest entry the first key.
 */
const MAX_CACHE_ENTRIES = 100;
const canonicalLocaleCache = new Map<string, string | undefined>();

const cacheCanonicalLocale = (locale: string, result: string | undefined): void => {
  if (canonicalLocaleCache.size >= MAX_CACHE_ENTRIES) {
    const oldest = canonicalLocaleCache.keys().next().value;
    if (oldest !== undefined) {
      canonicalLocaleCache.delete(oldest);
    }
  }
  canonicalLocaleCache.set(locale, result);
};

/**
 * Canonicalize a locale tag, returning `undefined` when it is unusable.
 *
 * Strips the POSIX decorations that some devices append to `navigator.language`
 * — modifiers ("en-US@posix", "@calendar=...") and charset suffixes ("en-US.UTF-8")
 * — then validates the result via `Intl.getCanonicalLocales`, which throws a
 * RangeError on anything invalid.
 */
const canonicalizeLocale = (locale?: string | null): string | undefined => {
  if (!locale || typeof locale !== 'string') {
    return undefined;
  }

  if (canonicalLocaleCache.has(locale)) {
    return canonicalLocaleCache.get(locale);
  }

  let result: string | undefined;
  try {
    // Underscore separators are deliberately NOT normalized: useIsImperialCultures
    // treats "en_US" as malformed and defers to the geo-based culture, and that
    // behaviour is already shipped and covered by its spec.
    const stripped = locale
      .trim()
      .split('@')[0] // "en-US@posix" -> "en-US"
      .split('.')[0]; // "en-US.UTF-8" -> "en-US"
    if (stripped) {
      result = Intl.getCanonicalLocales(stripped)[0];
    }
  } catch {
    // invalid tag; cache the miss so we don't re-parse it every render
  }

  cacheCanonicalLocale(locale, result);
  return result;
};

/**
 * Returns a canonical, valid BCP 47 locale tag, falling back to 'en-US' when the
 * tag is empty, missing, or not parseable.
 *
 * Use this when a locale is required. When an unparseable tag should instead defer
 * to some other signal (geo, event settings), use {@link tryParseLocaleRegion} and
 * handle `undefined` yourself — silently substituting 'en-US' would make every
 * affected user look like they are in the US.
 */
export const sanitizeLocale = (locale?: string | null): string => canonicalizeLocale(locale) ?? FALLBACK_LOCALE;

/**
 * Sanitize an array of locale tags, dropping unparseable entries so they cannot
 * outrank the valid ones that follow. Falls back to `['en-US']` when the array is
 * empty or every entry is invalid.
 */
export const sanitizeLocales = (locales?: ReadonlyArray<string> | null): string[] => {
  const cleaned = (locales ?? []).map(locale => canonicalizeLocale(locale)).filter((locale): locale is string => !!locale);
  return cleaned.length > 0 ? cleaned : [FALLBACK_LOCALE];
};

/**
 * Extract the uppercased region subtag from a locale tag, or `undefined` when the
 * tag is unparseable or carries no region. Never throws, and never invents a region
 * for an invalid tag — callers are expected to fall back to their own signal.
 */
export const tryParseLocaleRegion = (locale?: string | null): string | undefined => {
  const canonical = canonicalizeLocale(locale);
  if (!canonical) {
    return undefined;
  }
  try {
    // eslint-disable-next-line compat/compat, no-restricted-syntax -- this module is the sanctioned wrapper
    return new Intl.Locale(canonical).region?.toUpperCase();
  } catch {
    return undefined;
  }
};

/**
 * `undefined` is passed straight through rather than sanitized: it means "no locale
 * supplied", and the Intl constructors answer that with the runtime's default locale.
 * Substituting 'en-US' here would silently force US formatting on the call sites that
 * deliberately omit a tag.
 */
const sanitizeLocaleArg = (locales?: string | ReadonlyArray<string>): string | string[] | undefined => {
  // An empty array means the same as `undefined` -- "no locale supplied" -- so it gets the same
  // passthrough. sanitizeLocales() keeps its ['en-US'] fallback, because useBrowserLocale needs a
  // concrete tag; only the formatter wrappers defer to the runtime default.
  if (locales === undefined || (Array.isArray(locales) && locales.length === 0)) {
    return undefined;
  }
  return typeof locales === 'string' ? sanitizeLocale(locales) : sanitizeLocales(locales);
};

/**
 * Drop-in replacement for `new Intl.NumberFormat(locales, options)` that
 * sanitizes the locale first. Never throws on invalid locale input.
 */
export const safeNumberFormat = (locales?: string | ReadonlyArray<string>, options?: Intl.NumberFormatOptions): Intl.NumberFormat =>
  // eslint-disable-next-line no-restricted-syntax -- this module is the sanctioned wrapper
  new Intl.NumberFormat(sanitizeLocaleArg(locales), options);

/**
 * Drop-in replacement for `new Intl.DateTimeFormat(locales, options)` that
 * sanitizes the locale first. Never throws on invalid locale input.
 */
export const safeDateTimeFormat = (locales?: string | ReadonlyArray<string>, options?: Intl.DateTimeFormatOptions): Intl.DateTimeFormat =>
  // eslint-disable-next-line no-restricted-syntax -- this module is the sanctioned wrapper
  new Intl.DateTimeFormat(sanitizeLocaleArg(locales), options);
